/** Default foiling state options shown before API discovery completes. */
export const MANEUVER_DEFAULT_STATE_OPTIONS = ['H0', 'H1', 'H2'] as const;

/** Field names used for maneuver foiling state across API / parquet / live rows. */
const FOILING_STATE_FIELD_NAMES = [
  'State',
  'STATE',
  'state',
  'FOILING_STATE',
  'Foiling_state',
  'foiling_state',
  'FoilingState',
  'foilingState',
] as const;

/**
 * Trim + uppercase one state or event-type token; '' for null/undefined/blank.
 *
 * Shared so a state and the event type it belongs to are normalised the same way — the per-type
 * settings map is keyed on this, and `tack`, ` TACK ` and `TACK` must be one bucket, not three.
 */
export function normalizeManeuverState(value: unknown): string {
  if (value == null) return '';
  return String(value).trim().toUpperCase();
}

export function rowFoilingState(row: Record<string, unknown>): string | null {
  for (const key of FOILING_STATE_FIELD_NAMES) {
    const raw = row[key];
    if (raw == null) continue;
    const str = String(raw).trim();
    if (str !== '') return str.toUpperCase();
  }
  return null;
}

/** Collect unique foiling states from maneuver rows, sorted ascending. */
export function extractManeuverFoilingStates(rows: Record<string, unknown>[]): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const state = rowFoilingState(row);
    if (state) values.add(state);
  }
  return Array.from(values).sort();
}

export interface ReconcileManeuverStateOptions {
  /** When true and selected is empty, return all available states. */
  defaultAll?: boolean;
}

/**
 * Drop selections not in available; optionally default to all available when empty.
 */
export function reconcileManeuverStateSelection(
  selected: string[],
  available: string[],
  options?: ReconcileManeuverStateOptions
): string[] {
  const availableSet = new Set(available.map((s) => s.trim().toUpperCase()));
  const reconciled = selected
    .map((s) => String(s).trim().toUpperCase())
    .filter((s) => s !== '' && availableSet.has(s));

  if (reconciled.length > 0) return reconciled;
  if (options?.defaultAll && available.length > 0) return [...available];
  return [];
}

/** Trim and uppercase state filter values for API / filter payloads. */
export function normalizeManeuverStateFilterValues(values: string[]): string[] {
  return values
    .map((s) => normalizeManeuverState(s))
    .filter((s) => s !== '');
}

/** Parse comma-separated state filter string into normalized values. */
export function parseManeuverStateFilterString(value: string): string[] {
  if (!value || !value.trim()) return [];
  return normalizeManeuverStateFilterValues(value.split(','));
}

/** Active-filter pills for foiling state: ALL when none selected or every available state is selected. */
export function maneuverStateFilterPillValues(
  selected: string[],
  available: string[]
): string[] {
  const normalizedSelected = normalizeManeuverStateFilterValues(selected);
  const normalizedAvailable = normalizeManeuverStateFilterValues(available);
  if (normalizedAvailable.length === 0) {
    return normalizedSelected.length > 0 ? normalizedSelected : ['ALL'];
  }
  if (normalizedSelected.length === 0) return ['ALL'];
  const selectedSet = new Set(normalizedSelected);
  if (normalizedAvailable.every((s) => selectedSet.has(s))) return ['ALL'];
  return normalizedSelected;
}

export interface ApplyManeuverFoilingStateAfterFetchOptions {
  /** When true and no saved filter, select all available states. Live pages pass false. */
  defaultAllWhenNoSaved?: boolean;
  /**
   * Baseline state list (from unfiltered discovery). When set, used instead of rows so
   * filtered table fetches do not shrink state filter options.
   */
  availableStates?: string[];
}

/** Extract available states from rows and reconcile selection after a table fetch. */
export function applyManeuverFoilingStateAfterFetch(
  rows: Record<string, unknown>[],
  selected: string[],
  hasSavedStateFilter: boolean,
  options?: ApplyManeuverFoilingStateAfterFetchOptions
): { available: string[]; reconciled: string[] } {
  const available =
    options?.availableStates !== undefined
      ? normalizeManeuverStateFilterValues(options.availableStates)
      : extractManeuverFoilingStates(rows);
  const defaultAll = !hasSavedStateFilter && (options?.defaultAllWhenNoSaved !== false);
  const reconciled = reconcileManeuverStateSelection(selected, available, { defaultAll });
  return { available, reconciled };
}

/** Reconcile selection against a baseline option list (does not derive options from rows). */
export function reconcileManeuverStatesWithBaseline(
  selected: string[],
  baselineStateOptions: string[],
  hasSavedStateFilter: boolean,
  options?: Pick<ApplyManeuverFoilingStateAfterFetchOptions, 'defaultAllWhenNoSaved'>
): string[] {
  const defaultAll = !hasSavedStateFilter && (options?.defaultAllWhenNoSaved !== false);
  return reconcileManeuverStateSelection(selected, baselineStateOptions, { defaultAll });
}
